import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IpsRequestsService } from '@app/ips/services/ips-requests.service';
import { ModalQrScannerComponent } from '@app/shared/components';
import { IProductDescription } from '@app/shared/interfaces';
import { ScreeningService } from '@app/store/screening/screening.service';
import { ScreeningState } from '@app/store/screening/screening.state';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, map, tap, startWith, switchMap, catchError, EMPTY } from 'rxjs';

@Component({
  selector: 'app-step-imei',
  templateUrl: './step-imei.component.html',
  styleUrls: ['./step-imei.component.sass', '../../screening.sass'],
})
export class StepImeiComponent implements OnInit {
  @ViewChild('inputImei') searchElement: ElementRef;
  @Input() private nextStep: any;
  @Output() completedChange = new EventEmitter<boolean>();

  form: FormGroup = new FormGroup({
    idTrade: new FormControl(''),
    originalImei: new FormControl(''),
    confirmImei: new FormControl(''),
    productSku: new FormControl(''),
    imeiValidated: new FormControl(''),
  });

  produtList: IProductDescription[] = [];
  filteredProductList$: Observable<IProductDescription[]>;
  shouldInformProduct: boolean;
  notFoundProduct: IProductDescription = { description: 'Produto não encontrado' };

  constructor(
    public dialog: MatDialog,
    public _requestService: IpsRequestsService,
    private _screeningService: ScreeningService,
    private _toastrService: ToastrService,
    private store: Store,
    private _fb: FormBuilder,
    private _router: Router
  ) {
  }

  public imeiValue: string;
  idTrade: string;

  ngOnInit(): void {
    const { idTrade, originalIMEI } = this.store.selectSnapshot(ScreeningState);
    this.idTrade = idTrade;
    this.form = this._fb.group({
      idTrade: [idTrade, Validators.required],
      originalImei: [originalIMEI, Validators.required],
      confirmImei: ['', Validators.required],
      productSku: [''],
      imeiValidated: ['', Validators.requiredTrue],
    });

    this._requestService
      .getProductList()
      .pipe(
        map((productList: IProductDescription[]) =>
          productList.map((p: IProductDescription) => ({
            ...p,
            description: `${p.sku} - ${p.description}`,
          }))
        ),
        tap((productList) => {
          this.produtList = productList;
          this.form.get('productSku').updateValueAndValidity();
        })
      )
      .subscribe();

    this.filteredProductList$ = this.form.get('productSku').valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value.description)),
      map((description) =>
        description ? this._filterProduct(description) : this.produtList.slice()
      )
    );

    this.form.get('confirmImei').valueChanges.subscribe((_) => {
      this.shouldInformProduct = false;
      this.form.get('imeiValidated').patchValue(false);
    });
  }

  private _filterProduct(product: string) {
    const filterValue = product.toLowerCase();
    const results = this.produtList.filter((p) =>
      p.description.toLocaleLowerCase().includes(filterValue)
    );

    return results.length ? results : [this.notFoundProduct];
  }

  displayFn(product: IProductDescription) {
    return product ? product.description : '';
  }

  allowSelection(option: IProductDescription) {
    return { 'not-found': option.description === this.notFoundProduct.description };
  }

  readQrCode() {
    const dialogConfig = {
      width: '100%',
      maxWidth: '650px',
    };
    this.dialog.open(ModalQrScannerComponent, dialogConfig);
  }

  isMatch() {
    const originalImei = this.form.get('originalImei').value;
    const confimImei: string = this.form.get('confirmImei').value;
    this.form.get('confirmImei').markAsTouched();

    if (confimImei) {
      this._screeningService
        .isImeiBipedAvailable(confimImei, this.idTrade)
        .pipe(
          tap((response) => {
            this.form.get('imeiValidated').patchValue(true);
            if (response.isAvailable) {
              const myRegex = new RegExp(`\\b${originalImei}\\b`, 'i');
              if (!confimImei.match(myRegex)) {
                this.form
                  .get('productSku')
                  .setValidators([Validators.required, this.validateProductSku()]);
                this.form.get('productSku').updateValueAndValidity();
                this.shouldInformProduct = true;
                this._toastrService.info('Selecione um novo SKU para o Produto:');
              } else {
                this.shouldInformProduct = false;
              }
            }
          }),
          catchError((error) => {
            this.form.get('confirmImei').setErrors({ serverError: error.message });
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  updateProductCode() {
    const idTrade: string = this.form.get('idTrade').value;
    const selectedIProduct: IProductDescription = this.form.get('productSku').value;
    if (selectedIProduct) {
      this._requestService.updateProduct(idTrade, selectedIProduct).subscribe();
    }
  }

  sendImei() {
    const imei: string = this.form.get('confirmImei').value;

    if (this.form.valid) {
      this.updateProductCode();
      const updateImei$ = this._requestService.sendImei(imei, this.idTrade);
      const updateScreeingStep$ = this._screeningService.setStatusScreening(this.idTrade, 'imei');

      updateImei$.pipe(switchMap(() => updateScreeingStep$)).subscribe({
        next: () => {
          this.completedChange.emit(true);
          this.nextStep();
        },
        error: () => {
          this._toastrService.error('Não foi possível enviar o Imei.');
        },
      });
    }
  }
  resetScreening() {
    this._screeningService.resetScreening(this.idTrade).subscribe(() => {
      this._router.navigate(['ips', 'triagem', 'buscar']);
    });
  }

  validateProductSku(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const product: IProductDescription | string | null = control.value;
      if (typeof product === 'string') {
        return { productSkuInvlid: true };
      }

      if (product.idProduct) {
        return null;
      }
    };
  }
}
